/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  Alert,
  Dimensions,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome5,
} from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';

const { width } = Dimensions.get('window');

// ─── Connection ──────────────────────────────────────────────────────────────
const QZ_API_BASE = 'https://api.jobeng.ai';
const QZ_API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const qzStr = (v) => String(v ?? '').trim();
const qzArr = (v) => {
  if (Array.isArray(v)) return v;
  if (typeof v === 'string' && v.trim()) {
    try { const p = JSON.parse(v); if (Array.isArray(p)) return p; } catch {}
    return v.split(',').map((s) => s.trim()).filter(Boolean);
  }
  return [];
};

// Map a DB row from /api/data/quizzes to the quiz shape this screen uses.
const qzMapQuiz = (row, idx) => ({
  id: qzStr(row.id ?? row.quiz_id) || String(idx + 1),
  title: qzStr(row.title) || 'Quiz',
  course: qzStr(row.course) || qzStr(row.course_name) || '',
  type: qzStr(row.type) || 'Multiple Choice',
  dueDate: qzStr(row.due_date ?? row.dueDate) || '',
  duration: Number(row.duration ?? 0) || 0,
  questions: Number(row.questions ?? row.question_count ?? 0) || 0,
  attempts: Number(row.attempts ?? 0) || 0,
  maxAttempts: Number(row.max_attempts ?? row.maxAttempts ?? 3) || 3,
  status: qzStr(row.status) || 'Available',
  difficulty: qzStr(row.difficulty) || 'Medium',
  topics: qzArr(row.topics),
  instructor: qzStr(row.instructor) || '',
  passingScore: Number(row.passing_score ?? row.passingScore ?? 0) || 0,
  description: qzStr(row.description) || '',
});

const QuizScreen = () => {
  const navigation = useNavigation();
  const [userData, setUserData] = useState(null);
  const [selectedTab, setSelectedTab] = useState('Available');

  // Mixed color scheme for different quiz types
  const quizColors = {
    'Multiple Choice': '#f59e0b', // Orange
    'True/False': '#10b981', // Green
    'Fill in Blanks': '#3b82f6', // Blue
    'Essay Questions': '#8b5cf6', // Purple
    'Practical Test': '#ef4444', // Red
    'Case Study': '#06b6d4', // Cyan
    'Lab Assessment': '#f97316', // Orange variant
    'Final Exam': '#14b8a6', // Teal
  };

  const getColorForType = (type) => {
    return quizColors[type] || '#f59e0b';
  };

  useEffect(() => {
    loadUserData();
    loadQuizzes();
  }, []);

  const loadUserData = async () => {
    try {
      const storedUser = await AsyncStorage.getItem('user');
      const storedUserData = await AsyncStorage.getItem('userData');
      
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        setUserData(parsedUser);
      } else if (storedUserData) {
        const parsedUserData = JSON.parse(storedUserData);
        setUserData(parsedUserData);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  const getUserName = () => {
    if (!userData) return 'Student';
    return userData.full_name || userData.fullName || userData.name || 
           `${userData.first_name || userData.firstName || ''} ${userData.last_name || userData.lastName || ''}`.trim() || 
           'Student';
  };

  // Sample quizzes data
  // Curated seed shown until the quizzes table is populated.
  const SEED_QUIZZES = [
    {
      id: '1',
      title: 'Structural Analysis Quiz 1',
      course: 'Civil Engineering Fundamentals',
      type: 'Multiple Choice',
      dueDate: '2024-12-22',
      duration: 45,
      questions: 20,
      attempts: 2,
      maxAttempts: 3,
      status: 'Available',
      difficulty: 'Medium',
      topics: ['Beam Analysis', 'Load Calculations', 'Stress Distribution'],
      instructor: 'Dr. Sarah Johnson',
      passingScore: 70,
      description: 'Test your understanding of basic structural analysis principles.',
    },
    {
      id: '2',
      title: 'AutoCAD Commands Assessment',
      course: 'AutoCAD for Engineers',
      type: 'Practical Test',
      dueDate: '2024-12-25',
      duration: 60,
      questions: 15,
      attempts: 0,
      maxAttempts: 2,
      status: 'Available',
      difficulty: 'Hard',
      topics: ['Drawing Commands', '3D Modeling', 'Dimensioning'],
      instructor: 'Prof. Michael Chen',
      passingScore: 75,
      description: 'Practical assessment of AutoCAD skills and commands.',
    },
    {
      id: '3',
      title: 'Project Management Fundamentals',
      course: 'Project Management Essentials',
      type: 'Multiple Choice',
      dueDate: '2024-12-28',
      duration: 30,
      questions: 25,
      attempts: 1,
      maxAttempts: 3,
      status: 'Available',
      difficulty: 'Easy',
      topics: ['PRINCE2 Basics', 'Risk Management', 'Team Leadership'],
      instructor: 'Dr. Lisa Anderson',
      passingScore: 65,
      description: 'Quiz covering fundamental project management concepts.',
    },
    {
      id: '4',
      title: 'Safety Protocols Quiz',
      course: 'Health & Safety Fundamentals',
      type: 'True/False',
      dueDate: '2024-12-18',
      duration: 20,
      questions: 30,
      attempts: 3,
      maxAttempts: 3,
      status: 'Completed',
      difficulty: 'Easy',
      topics: ['Workplace Safety', 'Emergency Procedures', 'PPE Requirements'],
      instructor: 'Prof. Robert Taylor',
      passingScore: 80,
      description: 'Assessment of workplace safety knowledge.',
      completedDate: '2024-12-16',
      score: 92,
      grade: 'A',
      feedback: 'Excellent understanding of safety protocols!',
    },
    {
      id: '5',
      title: 'Materials Properties Test',
      course: 'Materials Science Basics',
      type: 'Fill in Blanks',
      dueDate: '2024-12-20',
      duration: 40,
      questions: 18,
      attempts: 2,
      maxAttempts: 2,
      status: 'Completed',
      difficulty: 'Medium',
      topics: ['Material Properties', 'Testing Methods', 'Failure Analysis'],
      instructor: 'Dr. Jennifer Lee',
      passingScore: 70,
      description: 'Test your knowledge of material properties and testing.',
      completedDate: '2024-12-19',
      score: 85,
      grade: 'B+',
      feedback: 'Good understanding, review failure analysis concepts.',
    },
    {
      id: '6',
      title: 'Python Programming Final Exam',
      course: 'Python Programming',
      type: 'Final Exam',
      dueDate: '2024-12-30',
      duration: 120,
      questions: 40,
      attempts: 0,
      maxAttempts: 1,
      status: 'Available',
      difficulty: 'Hard',
      topics: ['Data Structures', 'Algorithms', 'Object-Oriented Programming', 'Libraries'],
      instructor: 'Prof. Alex Rodriguez',
      passingScore: 75,
      description: 'Comprehensive final exam covering all Python programming concepts.',
    },
    {
      id: '7',
      title: 'Engineering Ethics Case Study',
      course: 'Professional Ethics',
      type: 'Essay Questions',
      dueDate: '2024-12-15',
      duration: 90,
      questions: 5,
      attempts: 1,
      maxAttempts: 1,
      status: 'Overdue',
      difficulty: 'Medium',
      topics: ['Professional Responsibility', 'Ethical Dilemmas', 'Code of Conduct'],
      instructor: 'Dr. Patricia Wilson',
      passingScore: 70,
      description: 'Analyze ethical scenarios in engineering practice.',
    },
  ];

  const [quizzes, setQuizzes] = useState(SEED_QUIZZES);

  // Fetch quizzes from the backend; keep the seed on failure/empty.
  const loadQuizzes = async () => {
    try {
      const res = await fetch(`${QZ_API_BASE}/api/data/quizzes?limit=200`, {
        headers: { 'x-api-key': QZ_API_KEY },
      }).then((r) => r.json()).catch(() => null);
      if (res && res.success && Array.isArray(res.data) && res.data.length) {
        setQuizzes(res.data.map(qzMapQuiz));
      }
    } catch (e) {
      // keep seed
    }
  };

  const tabs = ['Available', 'Completed', 'Overdue'];
  
  const filteredQuizzes = quizzes.filter(quiz => {
    return quiz.status === selectedTab;
  });

  const handleStartQuiz = (quiz) => {
    Alert.alert(
      'Start Quiz',
      `Start "${quiz.title}"?\n\nDuration: ${quiz.duration} minutes\nQuestions: ${quiz.questions}\nAttempts remaining: ${quiz.maxAttempts - quiz.attempts}`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Start Quiz',
          onPress: () => {
            Alert.alert('Quiz Started', `Good luck with "${quiz.title}"!\n\nRemember:\n• You have ${quiz.duration} minutes\n• Passing score is ${quiz.passingScore}%\n• This attempt will be recorded`);
          },
        },
      ]
    );
  };

  const handleViewResults = (quiz) => {
    Alert.alert(
      'Quiz Results',
      `${quiz.title}\n\nScore: ${quiz.score}%\nGrade: ${quiz.grade}\nCompleted: ${quiz.completedDate}\n\nFeedback: ${quiz.feedback}`,
      [{ text: 'OK' }]
    );
  };

  const handleRetakeQuiz = (quiz) => {
    const attemptsLeft = quiz.maxAttempts - quiz.attempts;
    if (attemptsLeft > 0) {
      Alert.alert(
        'Retake Quiz',
        `Retake "${quiz.title}"?\n\nAttempts remaining: ${attemptsLeft}\nBest score will be kept.`,
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Retake',
            onPress: () => {
              Alert.alert('Quiz Started', `Starting retake of "${quiz.title}". Good luck!`);
            },
          },
        ]
      );
    } else {
      Alert.alert('No Attempts Left', 'You have used all available attempts for this quiz.');
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Easy': return '#10b981';
      case 'Medium': return '#f59e0b';
      case 'Hard': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Available': return '#3b82f6';
      case 'Completed': return '#10b981';
      case 'Overdue': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const renderTabBar = () => (
    <View style={styles.tabBar}>
      {tabs.map((tab) => (
        <TouchableOpacity
          key={tab}
          style={[
            styles.tab,
            selectedTab === tab && [styles.activeTab, { backgroundColor: '#6366f1' }],
          ]}
          onPress={() => setSelectedTab(tab)}
        >
          <Text
            style={[
              styles.tabText,
              selectedTab === tab && styles.activeTabText,
            ]}
          >
            {tab}
          </Text>
          <View style={[
            styles.tabBadge,
            { backgroundColor: selectedTab === tab ? '#ffffff' : '#6366f1' }
          ]}>
            <Text style={[
              styles.tabBadgeText,
              { color: selectedTab === tab ? '#6366f1' : '#ffffff' }
            ]}>
              {quizzes.filter(q => q.status === tab).length}
            </Text>
          </View>
        </TouchableOpacity>
      ))}
    </View>
  );

  const renderQuizCard = (quiz) => {
    const quizColor = getColorForType(quiz.type);
    const statusColor = getStatusColor(quiz.status);
    const difficultyColor = getDifficultyColor(quiz.difficulty);
    
    return (
      <View key={quiz.id} style={styles.quizCard}>
        {/* Quiz Header */}
        <View style={styles.quizHeader}>
          <View style={[styles.quizIcon, { backgroundColor: quizColor }]}>
            <MaterialCommunityIcons 
              name={quiz.type === 'Multiple Choice' ? 'format-list-bulleted' :
                    quiz.type === 'True/False' ? 'check-circle' :
                    quiz.type === 'Fill in Blanks' ? 'form-textbox' :
                    quiz.type === 'Essay Questions' ? 'file-document-edit' :
                    quiz.type === 'Practical Test' ? 'tools' :
                    quiz.type === 'Case Study' ? 'briefcase-search' :
                    quiz.type === 'Lab Assessment' ? 'flask' :
                    quiz.type === 'Final Exam' ? 'school' : 'help-circle'} 
              size={24} 
              color="#ffffff" 
            />
          </View>
          <View style={styles.quizInfo}>
            <Text style={styles.quizTitle}>{quiz.title}</Text>
            <Text style={styles.quizCourse}>{quiz.course}</Text>
            <Text style={styles.quizInstructor}>by {quiz.instructor}</Text>
          </View>
          <View style={styles.statusContainer}>
            <View style={[styles.statusBadge, { backgroundColor: statusColor }]}>
              <Text style={styles.statusText}>{quiz.status}</Text>
            </View>
            <View style={[styles.difficultyBadge, { borderColor: difficultyColor }]}>
              <Text style={[styles.difficultyText, { color: difficultyColor }]}>{quiz.difficulty}</Text>
            </View>
          </View>
        </View>

        {/* Quiz Details */}
        <Text style={styles.quizDescription}>{quiz.description}</Text>

        <View style={styles.quizMetadata}>
          <View style={styles.metadataRow}>
            <View style={styles.metadataItem}>
              <MaterialCommunityIcons name="clock-outline" size={16} color="#6b7280" />
              <Text style={styles.metadataText}>{quiz.duration} minutes</Text>
            </View>
            <View style={styles.metadataItem}>
              <MaterialCommunityIcons name="help-circle" size={16} color="#6b7280" />
              <Text style={styles.metadataText}>{quiz.questions} questions</Text>
            </View>
          </View>
          
          <View style={styles.metadataRow}>
            <View style={styles.metadataItem}>
              <MaterialCommunityIcons name="calendar-clock" size={16} color="#6b7280" />
              <Text style={styles.metadataText}>Due: {quiz.dueDate}</Text>
            </View>
            <View style={styles.metadataItem}>
              <MaterialCommunityIcons name="target" size={16} color="#6b7280" />
              <Text style={styles.metadataText}>Pass: {quiz.passingScore}%</Text>
            </View>
          </View>
        </View>

        {/* Attempts Info */}
        <View style={styles.attemptsContainer}>
          <MaterialCommunityIcons name="refresh" size={16} color="#6b7280" />
          <Text style={styles.attemptsText}>
            Attempts: {quiz.attempts}/{quiz.maxAttempts}
          </Text>
          {quiz.status === 'Completed' && (
            <>
              <MaterialCommunityIcons name="trophy" size={16} color={quizColor} style={{ marginLeft: 16 }} />
              <Text style={[styles.scoreText, { color: quizColor }]}>
                Score: {quiz.score}% ({quiz.grade})
              </Text>
            </>
          )}
        </View>

        {/* Topics */}
        <View style={styles.topicsContainer}>
          <Text style={styles.topicsTitle}>Topics covered:</Text>
          <View style={styles.topicsGrid}>
            {quiz.topics.map((topic, index) => (
              <View key={index} style={[styles.topicTag, { borderColor: quizColor }]}>
                <Text style={[styles.topicText, { color: quizColor }]}>{topic}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* Actions */}
        <View style={styles.quizActions}>
          {quiz.status === 'Available' && (
            <>
              <TouchableOpacity
                style={[styles.actionButton, styles.startButton, { backgroundColor: quizColor }]}
                onPress={() => handleStartQuiz(quiz)}
              >
                <MaterialCommunityIcons name="play" size={16} color="#ffffff" />
                <Text style={styles.actionButtonText}>
                  {quiz.attempts > 0 ? 'Retake Quiz' : 'Start Quiz'}
                </Text>
              </TouchableOpacity>
            </>
          )}
          
          {quiz.status === 'Completed' && (
            <>
              <TouchableOpacity
                style={[styles.actionButton, styles.resultsButton, { backgroundColor: '#10b981' }]}
                onPress={() => handleViewResults(quiz)}
              >
                <MaterialCommunityIcons name="chart-line" size={16} color="#ffffff" />
                <Text style={styles.actionButtonText}>View Results</Text>
              </TouchableOpacity>
              
              {quiz.attempts < quiz.maxAttempts && (
                <TouchableOpacity
                  style={[styles.actionButton, styles.retakeButton, { borderColor: quizColor }]}
                  onPress={() => handleRetakeQuiz(quiz)}
                >
                  <MaterialCommunityIcons name="refresh" size={16} color={quizColor} />
                  <Text style={[styles.actionButtonText, { color: quizColor }]}>Retake</Text>
                </TouchableOpacity>
              )}
            </>
          )}
          
          {quiz.status === 'Overdue' && (
            <TouchableOpacity
              style={[styles.actionButton, styles.urgentButton, { backgroundColor: '#ef4444' }]}
              onPress={() => handleStartQuiz(quiz)}
            >
              <MaterialCommunityIcons name="alert" size={16} color="#ffffff" />
              <Text style={styles.actionButtonText}>Take Now</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    );
  };

  const renderSummaryStats = () => (
    <View style={styles.summaryCard}>
      <Text style={styles.summaryTitle}>Quiz Overview</Text>
      <Text style={styles.summarySubtitle}>Hello, {getUserName()}!</Text>
      
      <View style={styles.statsGrid}>
        <View style={styles.statItem}>
          <View style={[styles.statIcon, { backgroundColor: '#3b82f6' }]}>
            <MaterialCommunityIcons name="help-circle" size={20} color="#ffffff" />
          </View>
          <Text style={[styles.statNumber, { color: '#3b82f6' }]}>
            {quizzes.filter(q => q.status === 'Available').length}
          </Text>
          <Text style={styles.statLabel}>Available</Text>
        </View>
        
        <View style={styles.statItem}>
          <View style={[styles.statIcon, { backgroundColor: '#10b981' }]}>
            <MaterialCommunityIcons name="check-circle" size={20} color="#ffffff" />
          </View>
          <Text style={[styles.statNumber, { color: '#10b981' }]}>
            {quizzes.filter(q => q.status === 'Completed').length}
          </Text>
          <Text style={styles.statLabel}>Completed</Text>
        </View>
        
        <View style={styles.statItem}>
          <View style={[styles.statIcon, { backgroundColor: '#ef4444' }]}>
            <MaterialCommunityIcons name="clock-alert" size={20} color="#ffffff" />
          </View>
          <Text style={[styles.statNumber, { color: '#ef4444' }]}>
            {quizzes.filter(q => q.status === 'Overdue').length}
          </Text>
          <Text style={styles.statLabel}>Overdue</Text>
        </View>
        
        <View style={styles.statItem}>
          <View style={[styles.statIcon, { backgroundColor: '#6366f1' }]}>
            <MaterialCommunityIcons name="trophy" size={20} color="#ffffff" />
          </View>
          <Text style={[styles.statNumber, { color: '#6366f1' }]}>
            {Math.round(quizzes.filter(q => q.score).reduce((acc, q) => acc + q.score, 0) / quizzes.filter(q => q.score).length) || 0}%
          </Text>
          <Text style={styles.statLabel}>Avg Score</Text>
        </View>
      </View>
    </View>
  );

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="dark-content" backgroundColor="#f9fafb" />
      
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          onPress={() => navigation.goBack()}
          style={styles.backButton}
        >
          <Ionicons name="arrow-back" size={24} color="#6366f1" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Quizzes & Assessments</Text>
        <TouchableOpacity style={styles.menuButton}>
          <MaterialCommunityIcons name="dots-vertical" size={24} color="#6b7280" />
        </TouchableOpacity>
      </View>

      <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
        {renderSummaryStats()}
        {renderTabBar()}
        
        <View style={styles.quizzesSection}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>
              {selectedTab} Quizzes
            </Text>
            <Text style={styles.sectionCount}>{filteredQuizzes.length} quizzes</Text>
          </View>
          
          {filteredQuizzes.length > 0 ? (
            filteredQuizzes.map(renderQuizCard)
          ) : (
            <View style={styles.emptyState}>
              <MaterialCommunityIcons name="help-circle" size={64} color="#d1d5db" />
              <Text style={styles.emptyStateTitle}>No {selectedTab} Quizzes</Text>
              <Text style={styles.emptyStateText}>
                {selectedTab === 'Available' 
                  ? 'You have no available quizzes at the moment.' 
                  : selectedTab === 'Completed'
                  ? 'You haven\'t completed any quizzes yet.'
                  : 'Great! You have no overdue quizzes.'}
              </Text>
            </View>
          )}
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#f9fafb',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 15,
    paddingVertical: 12,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  backButton: {
    padding: 5,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#6366f1',
  },
  menuButton: {
    padding: 5,
  },
  container: {
    flex: 1,
  },
  summaryCard: {
    backgroundColor: '#fff',
    margin: 15,
    padding: 20,
    borderRadius: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 3,
  },
  summaryTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: 4,
  },
  summarySubtitle: {
    fontSize: 16,
    color: '#6b7280',
    marginBottom: 20,
  },
  statsGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  statItem: {
    alignItems: 'center',
    flex: 1,
  },
  statIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
  },
  statNumber: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 12,
    color: '#6b7280',
    textAlign: 'center',
  },
  tabBar: {
    flexDirection: 'row',
    backgroundColor: '#fff',
    marginHorizontal: 15,
    marginBottom: 15,
    borderRadius: 16,
    padding: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  tab: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
  },
  activeTab: {
    // backgroundColor set dynamically
  },
  tabText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#6b7280',
    marginRight: 6,
  },
  activeTabText: {
    color: '#ffffff',
    fontWeight: '600',
  },
  tabBadge: {
    minWidth: 20,
    height: 20,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  tabBadgeText: {
    fontSize: 10,
    fontWeight: 'bold',
  },
  quizzesSection: {
    paddingHorizontal: 15,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 15,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#111827',
  },
  sectionCount: {
    fontSize: 14,
    color: '#6b7280',
  },
  quizCard: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 16,
    marginBottom: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 3,
  },
  quizHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  quizIcon: {
    width: 48,
    height: 48,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  quizInfo: {
    flex: 1,
  },
  quizTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: 2,
  },
  quizCourse: {
    fontSize: 14,
    color: '#6b7280',
    marginBottom: 2,
  },
  quizInstructor: {
    fontSize: 12,
    color: '#9ca3af',
  },
  statusContainer: {
    alignItems: 'flex-end',
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 16,
    marginBottom: 4,
  },
  statusText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#ffffff',
  },
  difficultyBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 8,
    borderWidth: 1,
  },
  difficultyText: {
    fontSize: 9,
    fontWeight: '500',
  },
  quizDescription: {
    fontSize: 14,
    color: '#374151',
    lineHeight: 20,
    marginBottom: 12,
  },
  quizMetadata: {
    marginBottom: 12,
  },
  metadataRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  metadataItem: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  metadataText: {
    fontSize: 12,
    color: '#6b7280',
    marginLeft: 6,
  },
  attemptsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  attemptsText: {
    fontSize: 12,
    color: '#6b7280',
    marginLeft: 6,
  },
  scoreText: {
    fontSize: 12,
    fontWeight: '600',
    marginLeft: 6,
  },
  topicsContainer: {
    marginBottom: 16,
  },
  topicsTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 8,
  },
  topicsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  topicTag: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 16,
    borderWidth: 1,
    marginRight: 6,
    marginBottom: 4,
  },
  topicText: {
    fontSize: 10,
    fontWeight: '500',
  },
  quizActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    flex: 1,
    marginHorizontal: 2,
    justifyContent: 'center',
  },
  startButton: {
    // backgroundColor set dynamically
  },
  resultsButton: {
    // backgroundColor set dynamically
  },
  retakeButton: {
    backgroundColor: 'transparent',
    borderWidth: 1,
  },
  urgentButton: {
    // backgroundColor set dynamically
  },
  actionButtonText: {
    fontSize: 14,
    fontWeight: '500',
    marginLeft: 4,
    color: '#ffffff',
  },
  emptyState: {
    alignItems: 'center',
    padding: 40,
    backgroundColor: '#fff',
    borderRadius: 16,
    marginBottom: 15,
  },
  emptyStateTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#374151',
    marginTop: 16,
    marginBottom: 8,
  },
  emptyStateText: {
    fontSize: 14,
    color: '#6b7280',
    textAlign: 'center',
    lineHeight: 20,
  },
});

export default QuizScreen;

